import { useEffect, useState } from 'react';
import { Button, Spinner } from '@/components/ui';
import { ModalContentWrapper } from '@/components/ui';
import { useCreateBugReport } from '@/hooks/use-bug-reports';
import { cn } from '@/lib/cn';
import type { BugSeverity } from '@/types';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function BugReportModal({ open, onClose }: Props) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [expectedBehaviour, setExpectedBehaviour] = useState('');
  const [actualBehaviour, setActualBehaviour] = useState('');
  const [severity, setSeverity] = useState<BugSeverity>('MEDIUM');
  const { mutate: createBugReport, isPending } = useCreateBugReport();

  useEffect(() => {
    if (!open) {
      setTitle('');
      setDescription('');
      setStepsToReproduce('');
      setExpectedBehaviour('');
      setActualBehaviour('');
      setSeverity('MEDIUM');
    }
  }, [open]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !description.trim()) return;

    createBugReport(
      {
        title,
        description,
        stepsToReproduce: stepsToReproduce || undefined,
        expectedBehaviour: expectedBehaviour || undefined,
        actualBehaviour: actualBehaviour || undefined,
        severity,
        pageUrl: window.location.href,
        browserInfo: navigator.userAgent,
      },
      {
        onSuccess: () => {
          onClose();
          setTitle('');
          setDescription('');
          setStepsToReproduce('');
          setExpectedBehaviour('');
          setActualBehaviour('');
          setSeverity('MEDIUM');
        },
      },
    );
  }

  if (!open) return null;

  return (
    <ModalContentWrapper open={open} onClose={onClose} title="Report a Bug">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Title */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Bug Title</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Brief description of the bug..."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:ring-2 focus:ring-red-500"
            disabled={isPending}
          />
        </div>

        {/* Description */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="What went wrong?..."
            rows={3}
            className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:ring-2 focus:ring-red-500"
            disabled={isPending}
          />
        </div>

        {/* Steps to Reproduce */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Steps to Reproduce</label>
          <textarea
            value={stepsToReproduce}
            onChange={e => setStepsToReproduce(e.target.value)}
            placeholder="1. Click...&#10;2. Do...&#10;3. See..."
            rows={3}
            className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:ring-2 focus:ring-red-500"
            disabled={isPending}
          />
        </div>

        {/* Expected vs Actual */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Expected Behaviour
            </label>
            <textarea
              value={expectedBehaviour}
              onChange={e => setExpectedBehaviour(e.target.value)}
              placeholder="What should happen..."
              rows={2}
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:ring-2 focus:ring-red-500"
              disabled={isPending}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Actual Behaviour</label>
            <textarea
              value={actualBehaviour}
              onChange={e => setActualBehaviour(e.target.value)}
              placeholder="What actually happened..."
              rows={2}
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:ring-2 focus:ring-red-500"
              disabled={isPending}
            />
          </div>
        </div>

        {/* Severity */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Severity</label>
          <select
            value={severity}
            onChange={e => setSeverity(e.target.value as BugSeverity)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:ring-2 focus:ring-red-500"
            disabled={isPending}
          >
            <option value="LOW">Low - Minor UI issue</option>
            <option value="MEDIUM">Medium - Reduced functionality</option>
            <option value="HIGH">High - Major functionality broken</option>
            <option value="CRITICAL">Critical - Data loss or security issue</option>
            <option value="BLOCKER">Blocker - App unusable</option>
          </select>
        </div>

        {/* Metadata */}
        <div className="space-y-1 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
          <div>
            Page: <code className="font-mono">{window.location.href}</code>
          </div>
          <div>
            Browser:{' '}
            <code className="font-mono truncate">{navigator.userAgent.substring(0, 50)}...</code>
          </div>
        </div>
      </form>

      <div className="sticky bottom-0 flex items-center justify-end gap-3 rounded-b-lg border-t border-gray-200 bg-white px-6 py-4">
        <Button
          variant="secondary"
          type="button"
          onClick={onClose}
          disabled={isPending}
          className="w-fit"
        >
          Cancel
        </Button>
        <Button
          variant="secondary"
          type="submit"
          disabled={!title.trim() || !description.trim() || isPending}
          className={cn(
            'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors',
            isPending
              ? 'cursor-not-allowed bg-red-400'
              : 'bg-red-600 hover:bg-red-700 disabled:opacity-50',
          )}
        >
          {isPending && <Spinner size="lg" className="h-4 w-4 animate-spin" />}
          Submit Report
        </Button>
      </div>
    </ModalContentWrapper>
  );
}
